import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./App.css";
import Menus from "./components/Menus";
import About from "./components/About";
import Gallery from "./components/Gallery";
import Packages from "./components/Packages";
import { gtag } from "./utils/gtag";
import { CONTACT_EMAIL, MAILTO_URL } from "./utils/constants";
import { SiteImages } from "./utils/imagePaths";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
}

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/menus", label: "Our Menus" },
  { to: "/packages", label: "Packages" },
  { to: "/gallery", label: "Gallery" },
];

function NavLink({
  to,
  label,
  external,
  onClick,
}: {
  to: string;
  label: string;
  external?: boolean;
  onClick: () => void;
}) {
  const location = useLocation();
  const isActive = location.pathname === to;
  if (external) {
    return (
      <li>
        <a
          href={to}
          onClick={() => {
            gtag("event", "navigation", {
              event_category: "menu",
              event_label: label.toLowerCase().replace(/\s/g, "_"),
            });
            gtag("event", "enquire_click", {
              link_location: "nav_menu",
            });
            onClick();
          }}
        >
          {label}
        </a>
      </li>
    );
  }
  return (
    <li>
      <Link
        to={to}
        className={isActive ? "active" : ""}
        onClick={() => {
          gtag("event", "navigation", {
            event_category: "menu",
            event_label: label.toLowerCase().replace(/\s/g, "_"),
          });
          onClick();
        }}
      >
        {label}
      </Link>
    </li>
  );
}

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const copyrightYear = currentYear > 2025 ? `2025\u2013${currentYear}` : "2025";

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <div className="app">
        <header className="header">
          <nav className="nav">
            <div className="logo">Blossom &amp; Graze</div>
            <button className="mobile-menu-button" onClick={toggleMobileMenu}>
              ☰
            </button>
            <ul className={`nav-links ${isMobileMenuOpen ? "active" : ""}`}>
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  label={link.label}
                  onClick={closeMobileMenu}
                />
              ))}
              <li className="nav-enquire-mobile-item">
                <a
                  href={MAILTO_URL}
                  onClick={() => {
                    gtag("event", "enquire_click", {
                      link_location: "nav_cta",
                    });
                    closeMobileMenu();
                  }}
                >
                  Enquire Now
                </a>
              </li>
            </ul>
            <a
              href={MAILTO_URL}
              className="nav-enquire-btn"
              onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "nav_cta",
                });
                closeMobileMenu();
              }}
            >
              Enquire Now
            </a>
          </nav>
        </header>

        <Routes>
          <Route path="/about" element={<About />} />
          <Route path="/menus" element={<Menus />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route
            path="/"
            element={
              <main className="main">
                <Helmet>
                  <title>Blossom and Graze Bristol | Premium Grazing Boards & Event Catering</title>
                  <meta name="description" content="Blossom and Graze Bristol - Premium grazing boards, charcuterie, and event catering in Bristol. Perfect for weddings, corporate events, and special occasions. Locally sourced ingredients, artisanal cheeses, and beautiful presentation." />
                  <link rel="canonical" href="https://www.blossomandgraze.co.uk/" />
                  <meta property="og:url" content="https://www.blossomandgraze.co.uk/" />
                  <meta property="og:title" content="Blossom and Graze Bristol | Premium Grazing Boards & Event Catering" />
                  <meta property="og:description" content="Beautiful grazing boards and event catering in Bristol. Perfect for weddings, corporate events, and special occasions. Locally sourced ingredients and artisanal presentation." />
                </Helmet>
                {/* Hero Section */}
                <section className="hero">
                  <div className="hero-bg">
                    <img
                      src={SiteImages.homeHero}
                      alt="Premium artisanal grazing board with cheeses, cured meats, and seasonal fruits"
                    />
                  </div>
                  <div className="hero-content">
                    <h1>BLOSSOM AND GRAZE BRISTOL</h1>
                    <p>Premium Grazing Boards &amp; Event Catering in Bristol</p>
                    <div className="hero-cta-group">
                      <a
                        href={MAILTO_URL}
                        className="cta-button"
                        onClick={() => {
                          gtag("event", "enquire_click", {
                            link_location: "hero_cta",
                          });
                        }}
                      >
                        Enquire Now
                      </a>
                      <Link
                        to="/menus"
                        className="cta-button-outline"
                        onClick={() => {
                          gtag("event", "click", {
                            event_category: "cta",
                            event_label: "view_menus_home",
                          });
                        }}
                      >
                        View Menus
                      </Link>
                    </div>
                  </div>
                </section>

                {/* Feature Cards Section */}
                <section className="features">
                  <div className="feature-card">
                    <div className="feature-card-image tall">
                      <img
                        src={SiteImages.homeGrazingBoard}
                        alt="Artfully curated grazing board with premium cheeses and charcuterie"
                      />
                    </div>
                    <span className="feature-card-label">The Signature Choice</span>
                    <h3>Grazing Boards</h3>
                    <p>
                      Artfully curated boards featuring locally-sourced cheeses,
                      artisanal charcuterie, and seasonal botanical accents. Perfect
                      for intimate gatherings or grand celebrations.
                    </p>
                    <Link to="/menus" className="feature-card-link">
                      <span>Explore Boards</span>
                      <span className="material-symbols-outlined">arrow_forward</span>
                    </Link>
                  </div>

                  <div className="feature-card offset">
                    <div className="feature-card-image portrait">
                      <img
                        src={SiteImages.homeEventCatering}
                        alt="Elegant event catering spread for a celebration"
                      />
                    </div>
                    <span className="feature-card-label">Exquisite Service</span>
                    <h3>Event Catering</h3>
                    <p>
                      Sophisticated catering solutions for weddings, corporate events,
                      and private parties. We bring the garden to your table with
                      meticulous attention to detail.
                    </p>
                    <a href={MAILTO_URL} className="feature-card-link" onClick={() => {
                      gtag("event", "enquire_click", {
                        link_location: "catering_card",
                      });
                    }}>
                      <span>Catering Services</span>
                      <span className="material-symbols-outlined">arrow_forward</span>
                    </a>
                  </div>
                </section>

                {/* Quote Section */}
                <section className="quote-section">
                  <span className="material-symbols-outlined">local_florist</span>
                  <blockquote>
                    &ldquo;Crafting moments of connection through artisanal grazing and
                    botanical-inspired flavors.&rdquo;
                  </blockquote>
                </section>

                {/* Bento Gallery Grid */}
                <section className="bento-gallery">
                  <div className="bento-grid">
                    <div className="bento-large">
                      <img src={SiteImages.homeGallery1} alt="Welcome drinks station with refreshments for guests" />
                    </div>
                    <div className="bento-small">
                      <img src={SiteImages.homeGallery2} alt="Balloon hoop display for a celebration" />
                    </div>
                    <div className="bento-small">
                      <img src={SiteImages.homeGallery3} alt="Afternoon tea spread with sandwiches and treats" />
                    </div>
                    <div className="bento-wide">
                      <img src={SiteImages.homeGallery4} alt="Grazing table with charcuterie, cheese and accompaniments" />
                    </div>
                  </div>
                </section>
              </main>
            }
          />
        </Routes>

        <footer className="footer">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">Blossom &amp; Graze</div>
              <p>Bristol, United Kingdom &mdash; Serving Bristol and surrounding areas</p>
            </div>
            <div className="footer-links">
              <a href={MAILTO_URL} onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "footer",
                });
              }}>
                {CONTACT_EMAIL}
              </a>
              <a
                href="https://www.instagram.com/blossomandgrazebristol"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61575192150234"
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {copyrightYear} Blossom and Graze Bristol. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;

